import { defineCollection } from "@rebasepro/common";
import leadsCollection from "./leads.js";
/**
 * A person behind a lead — the unit outreach is actually addressed to.
 *
 * A `lead` is a public post where someone voiced a pain. A `prospect` is the
 * human who wrote it, resolved to a name, a company and (sometimes) an email.
 * The two are separate because one person can voice three pains, and because
 * the decision "is this worth an email" is about the person, not the post.
 *
 * ── About the email addresses ────────────────────────────────────────────────
 * This app has no contact database and buys no data. Every address here was
 * *published by its owner* — on a GitHub profile, or as a mailto: link on their
 * own site — and `email_source` records which. There is deliberately no
 * pattern-guessing (`first.last@company.com`): guessed addresses bounce, poison
 * a sending domain, and reach people who never published an address anywhere.
 * `verified` is not a state this codebase can produce, so it does not exist.
 */
/** A value as a trimmed string, or "" — display roles are text. */
const str = (value) => (typeof value === "string" ? value.trim() : value == null ? "" : String(value));
const prospectsCollection = defineCollection({
    // "People", not "Prospects" — see the note in leads.ts. The slug stays
    // `prospects` so existing rows and the `prospect_id` foreign key survive.
    name: "People",
    singularName: "Person",
    slug: "prospects",
    table: "prospects",
    description: "The human behind a signal — who you would actually write to. One person can have several signals. Addresses are published by their owner or absent, never guessed.",
    history: true,
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        lead: {
            name: "Signal",
            type: "relation",
            description: "The public post where this person voiced the pain",
            relation: {
                kind: "belongsTo",
                target: () => leadsCollection,
                relationName: "lead",
                localKey: "lead_id",
                onDelete: "cascade"
            }
        },
        handle: {
            name: "Handle",
            type: "string",
            validation: { required: true, unique: true },
            description: "Platform handle, namespaced as <source>:<handle> so hn:bob and github:bob stay distinct"
        },
        display_name: { name: "Name", type: "string" },
        role: { name: "Role", type: "string", description: "Self-described title, when they publish one" },
        company: { name: "Company", type: "string" },
        company_url: { name: "Company URL", type: "string", url: true },
        profile_url: { name: "Profile", type: "string", url: true },
        location: { name: "Location", type: "string" },
        email: {
            name: "Email",
            type: "string",
            description: "Only ever an address its owner published publicly. Blank is a normal, common state."
        },
        email_source: {
            name: "Email source",
            type: "string",
            defaultValue: "none",
            description: "Where the address came from. There is no 'verified' — nothing here verifies deliverability.",
            enum: [
                { id: "none", label: "No address found", color: "gray" },
                { id: "github_profile", label: "GitHub profile (public)", color: "green" },
                { id: "site_mailto", label: "mailto: on their site", color: "teal" },
                { id: "profile_bio", label: "Written in their public bio", color: "cyan" },
                { id: "devto_profile", label: "Site linked from dev.to", color: "purple" },
                { id: "manual", label: "Entered by hand", color: "blue" }
            ]
        },
        segment: {
            name: "Segment",
            type: "string",
            description: "Which ICP segment they fall in",
            enum: [
                { id: "product_team", label: "Product team", color: "blue" },
                { id: "agency", label: "Agency / freelancer", color: "purple" },
                { id: "internal_tools", label: "Internal tools", color: "green" },
                { id: "sql_migrator", label: "SQL migrator", color: "orange" },
                { id: "unknown", label: "Unknown", color: "gray" }
            ]
        },
        angle: {
            name: "Angle",
            type: "string",
            description: "Which offer angle from ICP.md fits them",
            enum: [
                { id: "A", label: "A — RLS done right, admin included", color: "blue" },
                { id: "B", label: "B — Ship the client an admin panel", color: "purple" },
                { id: "C", label: "C — Retool bill → zero", color: "green" }
            ]
        },
        fit_score: {
            name: "Fit",
            type: "number",
            validation: { min: 0, max: 5 },
            description: "0-5. Inherited from the lead, then adjusted by what enrichment found."
        },
        pain: {
            name: "Pain",
            type: "string",
            description: "One line: the problem they have. Carried over from the lead, and what the email opens on."
        },
        evidence: {
            name: "Evidence",
            type: "string",
            admin: { multiline: true },
            description: "The verbatim thing they said that makes them a prospect. Every email must be traceable to this."
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "found",
            enum: [
                { id: "found", label: "Found", color: "gray" },
                { id: "researching", label: "Researching", color: "yellow" },
                { id: "ready", label: "Ready to write", color: "blue" },
                { id: "drafted", label: "Drafted", color: "purple" },
                { id: "contacted", label: "Contacted", color: "green" },
                { id: "replied", label: "Replied", color: "teal" },
                { id: "won", label: "Won", color: "green" },
                { id: "lost", label: "Lost", color: "orange" },
                { id: "not_a_fit", label: "Not a fit", color: "gray" },
                { id: "do_not_contact", label: "Do not contact", color: "red" }
            ]
        },
        /**
         * Why a thread ended the way it did.
         *
         * The one field that turns a pipeline into something you can learn
         * from: `lost` on its own says a number went down, and says nothing
         * about which of the segments in ICP.md is not worth writing to.
         */
        outcome_reason: {
            name: "Outcome reason",
            type: "string",
            enum: [
                { id: "no_reply", label: "Never answered", color: "gray" },
                { id: "not_interested", label: "Not interested", color: "orange" },
                { id: "wrong_time", label: "Wrong time", color: "yellow" },
                { id: "happy_with_current", label: "Happy with what they have", color: "blue" },
                { id: "price", label: "Price", color: "red" },
                { id: "missing_feature", label: "Missing a feature", color: "purple" },
                { id: "signed_up", label: "Signed up", color: "green" },
                { id: "other", label: "Other", color: "gray" }
            ]
        },
        no_email_reason: {
            name: "Why no email",
            type: "string",
            admin: { readOnly: true },
            description: "Set by enrichment so a blank address is never a mystery"
        },
        found_at: { name: "Found at", type: "date", autoValue: "on_create", admin: { readOnly: true } },
        __order: { name: "Order", type: "string", admin: { disabled: true, hideFromCollection: true } }
    },
    admin: {
        icon: "Person",
        group: "Outreach",
        defaultViewMode: "list",
        enabledViews: ["list", "table", "kanban"],
        kanban: { columnProperty: "status" },
        orderProperty: "__order",
        /**
         * Half of these rows have no email, and that is the single fact that
         * decides what can be done with one — Reddit and Stack Overflow authors
         * can never be resolved to an address, so they are public-reply-only by
         * construction. A list that does not say so invites the same
         * disappointment on every card.
         *
         * The title falls back to the handle: a resolved person has a name, and
         * an unresolved one is still somebody, not a blank row.
         */
        display: {
            title: ({ entity }) => str(entity.values.display_name) || str(entity.values.handle),
            subtitle: ({ entity }) => [str(entity.values.role), str(entity.values.company)].filter(Boolean).join(" · ")
                || str(entity.values.pain),
            status: "status",
            date: "found_at",
            tags: ({ entity }) => [
                entity.values.email ? "has email" : str(entity.values.no_email_reason) || "no email",
                entity.values.fit_score ? `fit ${entity.values.fit_score}` : "",
                str(entity.values.segment)
            ].filter(Boolean)
        },
        sort: ["found_at", "desc"],
        propertiesOrder: [
            "display_name",
            "status",
            "outcome_reason",
            "email",
            "email_source",
            "company",
            "role",
            "segment",
            "angle",
            "fit_score",
            "pain",
            "evidence",
            "handle",
            "profile_url",
            "company_url",
            "location",
            "lead",
            "no_email_reason",
            "found_at",
            "__order"
        ],
        filterPresets: [
            { label: "Writable (has an address)", filterValues: { status: ["==", "ready"] } },
            { label: "Strong fit", filterValues: { fit_score: [">=", 4] } },
            { label: "Agencies", filterValues: { segment: ["==", "agency"] } }
        ]
    }
});
export default prospectsCollection;
